import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

import type { PermissionProfilePresetsQuery } from "@/app/access-presets/data_sources/services";
import { PermissionProfilePresetsStore } from "@/app/access-presets/data_sources/store";
import { ChipList, StatusBadge } from "@/app/library/components/molecules";
import type { PresetStatus } from "@/core/data_sources/models/APIPermissionProfilePreset";
import { APIPermissionProfilePreset } from "@/core/data_sources/models/APIPermissionProfilePreset";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import { adminSelectClasses, DataTable, PageHeader } from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";
import { parseLinkHeader } from "@/core/utils/pagination";

import { DisableEnableDialog } from "./components/DisableEnableConfirm";
import { DuplicatePresetDialog } from "./components/DuplicateDialog";
import {
  PresetEditorDialog,
  type PresetEditorMode,
} from "./components/PresetEditorDialog";
import { PresetUsageDrawer } from "./components/PresetUsageDrawer";

const STATUS_FILTERS = [
  { label: "All", value: undefined },
  { label: "Active", value: "active" as PresetStatus },
  { label: "Disabled", value: "disabled" as PresetStatus },
] as const;

export function AccessPresetsPage() {
  const { actions: appActions } = useStore(AppStore.instance);
  const { state, hooks } = useStore(PermissionProfilePresetsStore.instance);
  const [status, setStatus] = useState<PresetStatus | undefined>(undefined);
  const [compatibleRole, setCompatibleRole] = useState<string>("");
  const [context, setContext] = useState<string>("");
  const [searchInput, setSearchInput] = useState("");

  const { data, loading, query, setQuery } =
    hooks.usePermissionProfilePresetsStoreService({
      page: 1,
      page_size: 50,
    } as PermissionProfilePresetsQuery);

  const refresh = useCallback(() => {
    setQuery((prev) => ({ ...prev }));
  }, [setQuery]);

  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);

  const openEditor = useCallback(
    (mode: PresetEditorMode, preset?: APIPermissionProfilePreset) => {
      appActions.dialog(
        "open",
        <DialogTemplate className="max-w-none">
          <PresetEditorDialog
            mode={mode}
            onClose={closeDialog}
            onSaved={refresh}
            preset={preset}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, refresh],
  );

  const openView = useCallback(
    (preset: APIPermissionProfilePreset) => {
      appActions.dialog(
        "open",
        <DialogTemplate className="max-w-none">
          <PresetEditorDialog
            mode="view"
            onClose={closeDialog}
            onSaved={refresh}
            preset={preset}
            readonly
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, refresh],
  );

  const openDuplicate = useCallback(
    (preset: APIPermissionProfilePreset) => {
      appActions.dialog(
        "open",
        <DialogTemplate className="max-w-none">
          <DuplicatePresetDialog
            onClose={closeDialog}
            onSaved={refresh}
            preset={preset}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, refresh],
  );

  const openStatusToggle = useCallback(
    (preset: APIPermissionProfilePreset) => {
      appActions.dialog(
        "open",
        <DialogTemplate className="max-w-none">
          <DisableEnableDialog
            onClose={closeDialog}
            onSaved={refresh}
            preset={preset}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, refresh],
  );

  const openUsage = useCallback(
    (preset: APIPermissionProfilePreset) => {
      appActions.rightDrawer(
        "open",
        520,
        <PresetUsageDrawer
          onClose={() => appActions.rightDrawer("close")}
          preset={preset}
        />,
      );
    },
    [appActions],
  );

  const changeFilter = useCallback(
    (value: PresetStatus | undefined) => {
      setStatus(value);
      setQuery((prev) => {
        const next = { ...prev, page: 1 };
        if (value) {
          (next as PermissionProfilePresetsQuery).status = value;
        } else {
          delete (next as Record<string, unknown>).status;
        }
        return next as PermissionProfilePresetsQuery;
      });
    },
    [setQuery],
  );

  const changeCompatibleRole = useCallback(
    (value: string) => {
      setCompatibleRole(value);
      setQuery((prev) => {
        const next = { ...prev, page: 1 };
        if (value) {
          (next as PermissionProfilePresetsQuery).compatible_role =
            value as import("@/core/data_sources/models/APIPermissionProfilePreset").CompatibleRole;
        } else {
          delete (next as Record<string, unknown>).compatible_role;
        }
        return next as PermissionProfilePresetsQuery;
      });
    },
    [setQuery],
  );

  const changeContext = useCallback(
    (value: string) => {
      setContext(value);
      setQuery((prev) => {
        const next = { ...prev, page: 1 };
        if (value) {
          (next as PermissionProfilePresetsQuery).context =
            value as import("@/core/data_sources/models/APIPermissionProfilePreset").PresetContext;
        } else {
          delete (next as Record<string, unknown>).context;
        }
        return next as PermissionProfilePresetsQuery;
      });
    },
    [setQuery],
  );

  const changeSearch = useCallback(
    (value: string) => {
      setQuery((prev) => {
        const next = { ...prev, page: 1 };
        if (value) {
          (next as PermissionProfilePresetsQuery).search = value;
        } else {
          delete (next as Record<string, unknown>).search;
        }
        return next as PermissionProfilePresetsQuery;
      });
    },
    [setQuery],
  );

  const pagination = useMemo(() => parseLinkHeader(data?.header), [data]);
  const presets = state?.list ?? [];

  const columns = useMemo(
    () => [
      {
        header: "Preset",
        key: "preset",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          return (
            <div>
              <div className="font-extrabold text-ink">{d.name}</div>
              <div className="font-mono text-xs text-ink-muted">
                {d.preset_code} · v{d.version ?? 1}
              </div>
              <div className="mt-1 max-w-[320px] text-xs text-ink-muted">
                {d.description}
              </div>
            </div>
          );
        },
      },
      {
        header: "Compatible roles",
        key: "roles",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          return (
            <div className="flex flex-wrap gap-1">
              {(d.compatible_roles ?? []).map((role) => (
                <span
                  className="rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] font-extrabold text-ink-muted"
                  key={role}
                >
                  {role}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        header: "Contexts",
        key: "contexts",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          return (
            <div className="flex flex-wrap gap-1">
              {(d.contexts ?? []).map((ctx) => (
                <span
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-extrabold ${
                    ctx === "master"
                      ? "border-amber-300 bg-amber-50 text-amber-700"
                      : ctx === "vendor"
                        ? "border-red-300 bg-red-50 text-red-700"
                        : "border-border bg-surface text-ink-muted"
                  }`}
                  key={ctx}
                >
                  {ctx}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        header: "No. Caps",
        key: "caps",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          return (
            <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] font-extrabold text-ink-muted">
              {d.capabilities?.length ?? 0}
            </span>
          );
        },
      },
      {
        header: "Used by plans",
        key: "usage",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          const plansCount = d.usage_summary?.plans_count ?? 0;
          const assignments = d.usage_summary?.default_assignments_count ?? 0;
          if (plansCount === 0 && assignments === 0) {
            return <span className="text-xs text-ink-muted">— none —</span>;
          }
          return (
            <ChipList
              values={[
                plansCount
                  ? `${plansCount} plan${plansCount > 1 ? "s" : ""}`
                  : "",
                assignments
                  ? `${assignments} assignment` + (assignments === 1 ? "" : "s")
                  : "",
              ].filter(Boolean)}
            />
          );
        },
      },
      {
        header: "Updated At",
        key: "updated_at",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          return (
            <span className="text-xs text-ink-muted">
              {d.updated_at
                ? dayjs(d.updated_at).format("DD/MM/YYYY HH:mm:ss")
                : "—"}
            </span>
          );
        },
      },
      {
        header: "Status",
        key: "status",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          return <StatusBadge status={d.status} />;
        },
      },
      {
        className: "text-right",
        header: "Actions",
        key: "actions",
        render: (preset: APIPermissionProfilePreset) => {
          const d = preset.getData();
          return (
            <div className="flex flex-wrap justify-end gap-1.5">
              <Button
                onClick={() => openView(preset)}
                size="sm"
                variant="ghost"
              >
                View
              </Button>
              <Button onClick={() => openEditor("edit", preset)} size="sm">
                Edit
              </Button>
              <Button onClick={() => openDuplicate(preset)} size="sm">
                Duplicate
              </Button>
              <Button
                onClick={() => openStatusToggle(preset)}
                size="sm"
                variant={d.status === "active" ? "danger" : "primary"}
              >
                {d.status === "active" ? "Disable" : "Enable"}
              </Button>
              <Button onClick={() => openUsage(preset)} size="sm">
                Usage
              </Button>
            </div>
          );
        },
      },
    ],
    [openView, openEditor, openDuplicate, openStatusToggle, openUsage],
  );

  const activeCount = presets.filter(
    (p) => p.getData().status === "active",
  ).length;
  const disabledCount = presets.filter(
    (p) => p.getData().status === "disabled",
  ).length;

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={() => openEditor("new")} variant="primary">
            + New Preset
          </Button>
        }
        breadcrumb={["Governance", "Access Presets"]}
        subtitle="Global catalog of Permission Profile Presets. Plan Engine selects from this catalog; Tenant Setup installs copies."
        title="Permission Profile Presets"
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat
          label="Total presets"
          sub="In global catalog"
          value={presets.length}
        />
        <Stat
          label="Active"
          sub="Available to Plan Engine"
          tone="active"
          value={activeCount}
        />
        <Stat
          label="Disabled"
          sub="Cannot be selected"
          tone="muted"
          value={disabledCount}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-extrabold uppercase tracking-[0.06em] text-ink-muted">
          Filter:
        </span>
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.label}
            onClick={() => changeFilter(f.value)}
            size="sm"
            variant={status === f.value ? "primary" : "ghost"}
          >
            {f.label}
          </Button>
        ))}
        <select
          className={adminSelectClasses}
          onChange={(e) => changeCompatibleRole(e.target.value)}
          value={compatibleRole}
        >
          <option value="">All Roles</option>
          <option value="owner">Owner</option>
          <option value="supervisor">Supervisor</option>
          <option value="member">Member</option>
          <option value="viewer">Viewer</option>
        </select>
        <select
          className={adminSelectClasses}
          onChange={(e) => changeContext(e.target.value)}
          value={context}
        >
          <option value="">All Contexts</option>
          <option value="master">Master</option>
          <option value="workspace">Workspace</option>
          <option value="vendor">Vendor</option>
        </select>
        <input
          className="ml-auto w-64 rounded-control border border-border bg-surface px-3 py-1.5 text-[13px] text-ink"
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") changeSearch(searchInput);
          }}
          placeholder="Search by name"
          value={searchInput}
        />
      </div>

      <DataTable
        columns={columns}
        rowKey={(preset) =>
          String(preset.getData().id ?? preset.getData().preset_code)
        }
        rows={presets}
      />

      {loading ? <p className="mt-3 text-xs text-ink-muted">Loading…</p> : null}

      <div className="mt-4 flex items-center justify-between text-xs text-ink-muted">
        <span>Page {query.page}</span>
        <div className="flex gap-2">
          <Button
            disabled={!pagination.prev}
            onClick={() =>
              pagination.prev &&
              setQuery((prev) => ({ ...prev, page: pagination.prev! }))
            }
            size="sm"
          >
            Previous
          </Button>
          <Button
            disabled={!pagination.next}
            onClick={() =>
              pagination.next &&
              setQuery((prev) => ({ ...prev, page: pagination.next! }))
            }
            size="sm"
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
}

function Stat({
  label,
  sub,
  tone,
  value,
}: {
  readonly label: string;
  readonly sub: string;
  readonly tone?: "active" | "muted";
  readonly value: number;
}) {
  const valueColor =
    tone === "active"
      ? "text-emerald-600"
      : tone === "muted"
        ? "text-ink-muted"
        : "text-ink";
  return (
    <div className="rounded-panel border border-border bg-surface px-4 py-3">
      <div className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-ink-muted">
        {label}
      </div>
      <div className={`text-2xl font-extrabold ${valueColor}`}>{value}</div>
      <div className="text-xs text-ink-muted">{sub}</div>
    </div>
  );
}
